/**
 * The navigation rail. Structure and states follow FluidTalk's Sidebar; only the
 * accent and the destinations differ.
 *
 * Icons are drawn inline in the same stroke style as the ecosystem's icon set
 * (24px grid, 2px stroke, round caps) rather than pulling in an icon package
 * for five glyphs.
 */
import BrandMark, { Wordmark } from './BrandMark';
import { money } from '../lib/compose';

export type ScreenId = 'studio' | 'characters' | 'sets' | 'library' | 'gallery' | 'settings';

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const ICONS: Record<ScreenId | 'spend' | 'left' | 'right', JSX.Element> = {
  studio: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="m3 16 5-5 4 4" /><path d="m14 14 3-3 4 4" /><path d="M8.5 8.5h.01" /></>,
  characters: <><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  sets: <><rect x="3" y="4" width="18" height="5" rx="1" /><rect x="3" y="12" width="11" height="3" rx="1" /><rect x="3" y="18" width="15" height="3" rx="1" /></>,
  library: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
  gallery: <><path d="M18 22H4a2 2 0 0 1-2-2V6" /><rect x="6" y="2" width="16" height="16" rx="2" /><path d="m6 14 4-4 3 3" /><path d="m15 13 3-3 4 4" /></>,
  settings: <><path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3" /><path d="M14 2v4M8 10v4M16 18v4" /></>,
  spend: <><path d="M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" /><path d="M16 12h5v-2h-5a1 1 0 0 0 0 2Z" /></>,
  left: <><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></>,
  right: <><path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" /></>,
};

function Icon({ id }: { id: keyof typeof ICONS }) {
  return <svg viewBox="0 0 24 24" {...S}>{ICONS[id]}</svg>;
}

interface Group { label: string; items: { id: ScreenId; label: string; count?: number }[] }

export interface RailProps {
  current: ScreenId;
  onNavigate: (id: ScreenId) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  counts: { characters: number; sets: number; library: number; gallery: number };
  spentToday: number;
}

export default function Rail({
  current, onNavigate, collapsed, onToggleCollapsed, counts, spentToday,
}: RailProps) {
  const groups: Group[] = [
    { label: 'Create', items: [{ id: 'studio', label: 'Studio' }, { id: 'characters', label: 'Characters', count: counts.characters },
      { id: 'sets', label: 'Sets', count: counts.sets }] },
    { label: 'Images', items: [{ id: 'library', label: 'Library', count: counts.library }, { id: 'gallery', label: 'Gallery', count: counts.gallery }] },
  ];

  return (
    <div className="rail-wrap">
      <aside className="rail">
        <div className="rail-head">
          <div className="rail-brand">
            <div className="brand-lockup">
              <BrandMark size={28} />
              <span className="rail-hide"><Wordmark /></span>
            </div>
            <button
              className="collapse-btn"
              onClick={onToggleCollapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" {...S}>{collapsed ? ICONS.right : ICONS.left}</svg>
            </button>
          </div>
          <div className="rail-sub rail-hide">Local studio</div>
        </div>

        <nav className="nav">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="nav-label">{group.label}</div>
              <div className="nav-items">
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    className="nav-item"
                    title={item.label}
                    {...(current === item.id ? { 'aria-current': 'page' as const } : {})}
                    onClick={() => onNavigate(item.id)}
                  >
                    <Icon id={item.id} />
                    <span className="lbl rail-hide">{item.label}</span>
                    {item.count !== undefined && <span className="nav-count rail-hide">{item.count}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="rail-foot">
          <div className="spend-chip" title="Spent today">
            <Icon id="spend" />
            <span className="rail-hide" style={{ flex: 1 }}>
              <span className="mono" style={{ fontSize: 13.5, color: 'var(--fd-ink)' }}>{money(spentToday)}</span>
              <span className="faint mono" style={{ fontSize: 10 }}> today</span>
            </span>
          </div>
          <button
            className="nav-item"
            title="Settings"
            {...(current === 'settings' ? { 'aria-current': 'page' as const } : {})}
            onClick={() => onNavigate('settings')}
          >
            <Icon id="settings" />
            <span className="lbl rail-hide">Settings</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
